import React, { useEffect, useState } from "react";
import { CalendarCheck, Clock, PieChart, Star, Layers, Info, AlertTriangle, BookOpen } from "lucide-react";
import { ScoreGauge } from "../ScoreGauge";
import { riskTone, Badge } from "../PortalUI";
import { formatDate } from "../../../lib/api";
import { T, reduceMotion } from "./theme";

const ICON = { on_time_rate: CalendarCheck, relationship: Clock, utilization: PieChart, paid_sales: Star, settled: Layers };

const useCountUp = (target) => {
  const [v, setV] = useState(reduceMotion() ? target : 0);
  useEffect(() => {
    if (reduceMotion()) { setV(target); return undefined; }
    let raf; const t0 = performance.now();
    const step = (t) => { const p = Math.min(1, (t - t0) / 900); setV(Math.round(target * (1 - Math.pow(1 - p, 3)))); if (p < 1) raf = requestAnimationFrame(step); };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return v;
};

export const ScoreMainCard = ({ s, onExplain }) => {
  const tone = riskTone(s.risk);
  const shown = useCountUp(Number(s.score || 0));
  return (
    <section className="rounded-[26px] bg-white p-5 shadow-[0_18px_40px_-22px_rgba(42,14,79,0.45)]" data-testid="score-main-card">
      <div className="flex items-center justify-between">
        <h2 className="text-[17px] font-extrabold" style={{ color: T.deep }}>Meu score</h2>
        <Badge tone={tone} testid="score-main-label">{s.label}</Badge>
      </div>
      <div className="mt-2 grid gap-4 sm:grid-cols-[1.1fr_1fr] sm:items-center">
        <div>
          <ScoreGauge score={shown} color={tone.fg} />
          <div className="-mt-4 text-center text-[13px]" style={{ color: T.muted }} data-testid="score-main-updated">
            <Info size={13} className="mr-1 inline" />Última avaliação em {formatDate(s.updated_at)}
          </div>
          {s.stale && (
            <div className="mx-auto mt-2 flex w-fit items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-semibold"
              style={{ background: "#FFF3E0", color: "#A86200" }} data-testid="score-stale-warning">
              <AlertTriangle size={13} /> Avaliação em atualização · pode não refletir pagamentos recentes
            </div>
          )}
        </div>
        <ul className="space-y-2" data-testid="score-indicators">
          {s.indicators.length === 0 && <li className="text-[14px]" style={{ color: T.muted }}>Indicadores disponíveis após suas primeiras compras.</li>}
          {s.indicators.map((it) => {
            const Icon = ICON[it.key] || Star;
            return (
              <li key={it.key} className="flex items-center gap-3 rounded-2xl px-3 py-2.5" style={{ background: T.lavender }} data-testid={`score-indicator-${it.key}`}>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white" style={{ color: T.violet }}><Icon size={18} /></span>
                <span className="min-w-0 flex-1 text-[14px] font-semibold" style={{ color: T.deep }}>{it.label}</span>
                <span className="text-[16px] font-extrabold tabular-nums" style={{ color: T.violet }}>{it.value}{it.unit === "%" ? "%" : it.unit ? ` ${it.unit}` : ""}</span>
              </li>
            );
          })}
        </ul>
      </div>
      <button onClick={onExplain} className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl border-2 py-3 text-[15px] font-bold transition-colors hover:bg-[#F4EEFC]"
        style={{ borderColor: T.violet, color: T.violet }} data-testid="score-explain-btn">
        <BookOpen size={18} /> Entenda seu score
      </button>
    </section>
  );
};
